import React, { forwardRef } from 'react'

import styled from '@emotion/styled'
import { Controller, useFormContext } from 'react-hook-form'

import { size } from '@arcade/web'
import {
  ArcadeRadioGroup,
  ArcadeRadioInline,
  BaseCellAsset,
  PaddingInset,
  RadioCell,
  RadioInput,
  RadioInputCard,
  RadioInputInline,
  RadioInputProps,
  spacing,
  Stack,
} from '@cash-design-system/react'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'

import { ExclusiveOr } from '../util/types'
import { FieldController } from './FieldController/FieldController'
const ErrorText = styled.div`
  color: ${props => props.theme.colors.errorPressed};
  ${({ theme }) => theme.typography.helpText};
  margin-top: 10px;
  min-height: 20px;
`

const StyledRadioInput = styled(RadioInput)`
  cursor: pointer;
`

const LineUp = styled.div<{ numOptions: number; rounded?: boolean }>`
  display: grid;
  grid-template-columns: ${props => '1fr '.repeat(props.numOptions)};
  background-color: ${props => props.theme.colors.segmentedControlBackground};
  border-radius: ${props => (props.rounded ? props.theme.border.radius.pill : size[3])};
  padding: ${({ theme }) => theme.spacing.xxs};
`

export type RadioGroupOption = {
  label: React.ReactNode
  value: string
}

export type ArcadeRadioGroupOption = {
  label: string
  value: string
  cellProps?: {
    body?: React.ReactNode
    asset?: BaseCellAsset
  }
}

type RadioGroupOptionType = ExclusiveOr<RadioGroupOption, ArcadeRadioGroupOption>

export type RadioGroupProps = {
  name: string
  options: RadioGroupOptionType[]
  rules?: React.ComponentProps<typeof Controller>['rules'] // https://react-hook-form.com/api/useform/register#options
  onChange?: () => void
  isSegmentedControl?: boolean
  inputStyle?: 'inline' | 'input-card' | 'cell' | 'image'
  StyledLabelComponent?: RadioInputProps['StyledLabelComponent']
  defaultValue?: string
  InputWrapper?: React.FunctionComponent<{ children: React.ReactNode }>
  rounded?: boolean
}

export type RadioGroupOptionProps = { value: string; name: string } & RadioGroupProps

const RadioGroupOptions = forwardRef<HTMLDivElement, RadioGroupOptionProps>(
  (
    { options, value, name, isSegmentedControl, inputStyle = 'default', InputWrapper = Stack, rounded, ...rest },
    ref
  ) => {
    const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

    const RadioGroupInput = inputStyle === 'inline' ? RadioInputInline : StyledRadioInput
    const ArcadeRadioGroupInput =
      inputStyle === 'inline' ? ArcadeRadioInline : inputStyle === 'cell' ? RadioCell : RadioInputCard

    const inputs = options.map(item => {
      if (isArcadeMigrationEnabled && !isSegmentedControl) {
        const itemAsArcadeRadioGroupOption = item as ArcadeRadioGroupOption
        return (
          <ArcadeRadioGroupInput
            {...rest}
            key={item.value}
            data-testid={`arcade-${name}-${item.value}`}
            label={itemAsArcadeRadioGroupOption.label}
            cellProps={item.cellProps}
            value={item.value}
            name={name}
            checked={item.value === value}
            id={item.value}
          />
        )
      }
      return (
        <RadioGroupInput
          {...rest}
          key={item.value}
          data-testid={`${name}-${item.value}`}
          label={item.label}
          value={item.value}
          name={name}
          checked={item.value === value}
          id={item.value}
        />
      )
    })

    return isSegmentedControl ? (
      <LineUp data-testid={`segmented-control-${name}`} ref={ref} numOptions={inputs.length} rounded={rounded}>
        {inputs}
      </LineUp>
    ) : (
      <InputWrapper ref={ref} space={inputStyle === 'cell' ? spacing.xl : spacing.m}>
        {inputs}
      </InputWrapper>
    )
  }
)

export function RadioGroup({ name, rules, defaultValue, ...props }: RadioGroupProps): React.ReactElement {
  const { control } = useFormContext()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')
  if (isArcadeMigrationEnabled) {
    return (
      <FieldController
        FieldComponent={ArcadeRadioGroup}
        componentProps={{
          ...props,
          radioStyle: props.inputStyle ?? 'input-card',
          options: props.options as ArcadeRadioGroupOption[],
          CustomOptionsWrapper: props.InputWrapper,
        }}
        name={name}
        defaultValue={defaultValue}
        rules={rules}
      />
    )
  }

  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      rules={rules}
      control={control}
      render={({ field, fieldState: { error } }) => {
        return (
          <div>
            <RadioGroupOptions {...field} {...props} />
            {error?.message && (
              <PaddingInset>
                <ErrorText>{error?.message}</ErrorText>
              </PaddingInset>
            )}
          </div>
        )
      }}
    />
  )
}
